import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Info, Loader2 } from "lucide-react";
import { findById, gbp, vehicles } from "@/lib/inventory";
import { DemoSent } from "@/components/enquiry/DemoSent";
import { DEALER } from "@/data/dealer";

const schema = z.object({
  name: z.string().trim().min(2, "Please enter your name."),
  email: z.string().trim().email("Please enter a valid email address."),
  phone: z
    .string()
    .trim()
    .refine((v) => !v || /^[+()\d\s-]{10,17}$/.test(v), "Please enter a valid UK phone number, or leave this blank."),
  vehicleId: z.string(),
  preferredTime: z.string(),
  message: z.string().trim().min(10, "Please add a short message (at least 10 characters)."),
  website: z.string().optional(),
});

const label = (v) => `${v.year} ${v.make} ${v.model} – ${gbp(v.price)}`;

const defaultMessage = (v, mode) => {
  if (!v) return "";
  const car = `${v.year} ${v.make} ${v.model} ${v.variant} (${gbp(v.price)})`;
  return mode === "viewing" ? `Hi, I'd like to arrange a viewing of the ${car}.` : `Hi, I'm interested in the ${car}. Is it still available?`;
};

const Field = ({ id, label: text, error, optional, children }) => (
  <div className="flex flex-col gap-1.5">
    <label htmlFor={id} className="field-label">
      {text} {optional && <span className="font-normal text-steel">(optional)</span>}
    </label>
    {children}
    {error && (
      <p id={`${id}-error`} role="alert" className="text-sm font-medium text-[#B91C1C]">
        {error}
      </p>
    )}
  </div>
);

export const EnquiryForm = ({ defaultVehicleId = "", mode = "enquiry", prefix = "enquiry" }) => {
  const [sent, setSent] = useState(false);
  const [failure, setFailure] = useState("");
  const initial = findById(defaultVehicleId);
  const { register, handleSubmit, reset, formState } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { name: "", email: "", phone: "", vehicleId: initial ? initial.id : "", preferredTime: "", message: defaultMessage(initial, mode), website: "" },
  });
  const { errors, isSubmitting } = formState;

  const onSubmit = async (data) => {
    setFailure("");
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/enquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...data, mode, website: data.website || "" }),
      });
      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        throw new Error(typeof json?.detail === "string" ? json.detail : "");
      }
      setSent(true);
      toast.success(mode === "viewing" ? "Viewing request sent to YK Motors." : "Enquiry sent to YK Motors.");
    } catch (e) {
      setFailure(e.message || `Sorry, your message couldn't be sent. Please call ${DEALER.phone.display}.`);
    }
  };

  if (sent)
    return (
      <DemoSent
        mode={mode}
        prefix={prefix}
        onReset={() => {
          reset();
          setSent(false);
        }}
      />
    );

  const reg = (name) => ({
    id: `${prefix}-${name}`,
    "aria-invalid": errors[name] ? "true" : "false",
    "aria-describedby": errors[name] ? `${prefix}-${name}-error` : undefined,
    "data-testid": `${prefix}-${name}-input`,
    ...register(name),
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="grid gap-5" data-testid={`${prefix}-form`}>
      <div className="grid gap-5 sm:grid-cols-2">
        <Field id={`${prefix}-name`} label="Name" error={errors.name?.message}>
          <input type="text" autoComplete="name" className="field" {...reg("name")} />
        </Field>
        <Field id={`${prefix}-email`} label="Email" error={errors.email?.message}>
          <input type="email" autoComplete="email" className="field" {...reg("email")} />
        </Field>
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <Field id={`${prefix}-phone`} label="Phone" optional error={errors.phone?.message}>
          <input type="tel" autoComplete="tel" className="field" {...reg("phone")} />
        </Field>
        {mode === "viewing" ? (
          <Field id={`${prefix}-preferredTime`} label="Preferred time" optional>
            <select className="field" {...reg("preferredTime")}>
              <option value="">No preference</option>
              <option>Weekday daytime</option>
              <option>Weekday evening</option>
              <option>Weekend</option>
              <option>Live video viewing</option>
            </select>
          </Field>
        ) : (
          <Field id={`${prefix}-vehicleId`} label="Vehicle of interest" optional>
            <select className="field" {...reg("vehicleId")}>
              <option value="">No specific vehicle</option>
              {vehicles.map((v) => (
                <option key={v.id} value={v.id}>
                  {label(v)}
                </option>
              ))}
            </select>
          </Field>
        )}
      </div>
      {mode === "viewing" && (
        <Field id={`${prefix}-vehicleId`} label="Vehicle" optional>
          <select className="field" {...reg("vehicleId")}>
            <option value="">No specific vehicle</option>
            {vehicles.map((v) => (
              <option key={v.id} value={v.id}>
                {label(v)}
              </option>
            ))}
          </select>
        </Field>
      )}
      <Field id={`${prefix}-message`} label="Message" error={errors.message?.message}>
        <textarea rows={4} className="field" {...reg("message")} />
      </Field>
      <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" {...register("website")} />
      {failure && (
        <p role="alert" className="rounded-md border border-[#F1C4C4] bg-[#FDF2F2] px-4 py-3 text-sm font-medium text-[#B91C1C]" data-testid={`${prefix}-error`}>
          {failure}
        </p>
      )}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="flex items-start gap-2 text-sm text-steel">
          <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          Sent straight to the YK Motors inbox.
        </p>
        <button type="submit" disabled={isSubmitting} className="btn btn-primary w-full sm:w-auto" data-testid={`${prefix}-submit-button`}>
          {isSubmitting && <Loader2 className="animate-spin" aria-hidden="true" />}
          {mode === "viewing" ? "Request a viewing" : "Send enquiry"}
        </button>
      </div>
    </form>
  );
};
